import Vue from 'vue';
import axios from 'axios';
import config from '@/config';
import { getSelectedVenue } from '@/services/helpers';
import { client } from '@/services';
import apiClient from '@/services/apiClient';

const devicesService = client.service('devices');
const devices = {
  namespaced: true,
  state: {
    devices: [],
    paginatedDevices: [],
    paginatedTotal: 0,
    downloadingLogs: {},
    deviceDownloads: {},
    downloadThrottle: {},
    presenceMap: {}
  },
  getters: {
    GET_DEVICES: state => {
      var distinct = [];

      state.devices.forEach(device => {
        if (device.venueId == getSelectedVenue()._id) {
          if (distinct.length > 0) {
            if (!distinct.some(item => item._id === device._id)) {
              distinct.push(device);
            }
          } else {
            distinct.push(device);
          }
        }
      });

      return distinct;
    },
    IS_DOWNLOADING_LOGS: state => deviceId => {
      return state.downloadingLogs[deviceId] === true;
    },
    GET_DEVICE_DOWNLOADS: state => state.deviceDownloads,
    IS_DEVICE_DOWNLOADING: state => deviceId => {
      const dl = state.deviceDownloads[deviceId];
      return !!(dl && dl.status === 'downloading');
    },
    GET_DOWNLOAD_PROGRESS: state => deviceId => {
      const dl = state.deviceDownloads[deviceId];
      return dl ? dl.overallProgress : 0;
    },
    GET_DOWNLOAD_ITEMS: state => deviceId => {
      const dl = state.deviceDownloads[deviceId];
      return dl ? dl.items : [];
    },
    GET_DOWNLOAD_SPEED: state => deviceId => {
      const dl = state.deviceDownloads[deviceId];
      return dl ? dl.speed : 0;
    },
    GET_DOWNLOAD_THROTTLE: state => deviceId => {
      return state.downloadThrottle[deviceId] || 0;
    },
    GET_PAGINATED_DEVICES: state => state.paginatedDevices,
    GET_PAGINATED_TOTAL: state => state.paginatedTotal,
    IS_ONLINE: function(state) {
      return function(serial) {
        if (!serial) return false;
        return state.presenceMap[serial] === true;
      };
    }
  },
  mutations: {
    SET_DEVICES(state, devices) {
      state.devices = devices;
    },
    ADD_MORE_DEVICES(state, devices) {
      state.devices = Array.from(new Set(state.devices.concat(devices)));
    },
    ADD_DEVICE(state, device) {
      state.devices.push(device);
    },
    UPDATE_DEVICE(state, device) {
      const i = state.devices.findIndex(d => d._id === device._id);

      if (i !== -1) {
        Vue.set(state.devices, i, device); // reactivity hack
      }
    },
    UPDATE_DEVICE_STATUS(state, data) {
      const i = state.devices.findIndex(d => d.serial === data.client_id);
      const device = state.devices[i];
      device.status = Object.assign({}, data);
      Vue.set(state.devices, i, device); // reactivity hack
    },
    REMOVE_DEVICE(state, _id) {
      const i = state.devices.findIndex(d => d._id === _id);

      if (i > 0) {
        state.devices.splice(i, 1);
      }
    },
    SET_DOWNLOADING_LOGS(state, { deviceId, isDownloading }) {
      if (isDownloading) {
        state.downloadingLogs = { ...state.downloadingLogs, [deviceId]: true };
      } else {
        const newDownloadingLogs = { ...state.downloadingLogs };
        delete newDownloadingLogs[deviceId];
        state.downloadingLogs = newDownloadingLogs;
      }
    },
    SET_DEVICE_DOWNLOAD(state, { deviceId, downloadState }) {
      Vue.set(state.deviceDownloads, deviceId, downloadState);
    },
    DELETE_DEVICE_DOWNLOAD(state, deviceId) {
      Vue.delete(state.deviceDownloads, deviceId);
    },
    UPDATE_DEVICE_DOWNLOAD_ITEM(state, { deviceId, cloned }) {
      Vue.set(state.deviceDownloads, deviceId, cloned);
    },
    SET_DOWNLOAD_THROTTLE(state, { deviceId, timestamp }) {
      state.downloadThrottle[deviceId] = timestamp;
    },
    CLEAR_DOWNLOAD_THROTTLE(state, deviceId) {
      delete state.downloadThrottle[deviceId];
    },
    SET_PAGINATED_DEVICES(state, { data, total }) {
      state.paginatedDevices = data;
      state.paginatedTotal = total;
    },
    UPDATE_PAGINATED_DEVICE(state, device) {
      const i = state.paginatedDevices.findIndex(d => d._id === device._id);
      if (i !== -1) {
        Vue.set(state.paginatedDevices, i, device);
      }
    },
    CLEAR_PAGINATED_DEVICES(state) {
      state.paginatedDevices = [];
      state.paginatedTotal = 0;
    },
    SET_PRESENCE: function(state, map) {
      state.presenceMap = Object.assign({}, map);
    },
    UPDATE_PRESENCE: function(state, payload) {
      // Vue.set on the EXISTING object instead of replacing state.presenceMap
      // wholesale — a full replace re-triggers every component that has ever
      // read presenceMap (i.e. every row in the devices table) and forces Vue
      // to re-walk/re-observe the entire object. Vue.set updates/adds just this
      // one reactive key, so only rows depending on this specific serial are
      // affected.
      Vue.set(state.presenceMap, payload.serial, payload.online);
    },
    UPDATE_PRESENCE_BATCH: function(state, updates) {
      Object.keys(updates).forEach(function(serial) {
        Vue.set(state.presenceMap, serial, updates[serial]);
      });
    }
  },
  actions: {
    // store/devices.js — GET_DEVICES
    async GET_DEVICES({ commit, dispatch }) {
      const [groups, data] = await Promise.all([
        dispatch('groups/GET_GROUPS', null, { root: true }),
        devicesService.find({
          query: {
            venueId: getSelectedVenue()._id,
            $paginate: { value: false }
          }
        })
      ]).catch(err => {
        console.error('[GET_DEVICES] Promise.all failed:', err);
        throw err;
      });

      const devices = data.map(device => {
        const i = groups.findIndex(g => g._id === device.groupId);
        device.groupName = i >= 0 ? groups[i].name : 'No group yet.';
        return device;
      });

      commit('SET_DEVICES', devices);
      return devices;
    },

    async GET_UNASSIGNED_DEVICES({ rootState }) {
      const devices = await devicesService.find({
        query: {
          $select: [
            '_id',
            'serial',
            'name',
            'address',
            'ipAddress',
            'connected',
            'disconnected',
            'venueId'
          ],
          $paginate: {
            value: false
          }
        }
      });

      const normalized = (Array.isArray(devices) ? devices : devices.data || []).map(d => ({
        ...d,
        address: d.address || d.ipAddress || null
      }));

      //	const selectedVenue = getSelectedVenue();
      //console.log('selectedVenue', selectedVenue);
      return normalized.filter(device => !device.venueId);
    },
    async GET_DASHBOARD_DEVICES({ commit, dispatch }) {
      const devices = await devicesService.find({
        query: {
          showInDashboard: true,
          $paginate: {
            value: false
          }
        }
      });

      commit('SET_DEVICES', devices);

      return devices;
    },
    async GET_MORE_DEVICES({ commit, dispatch, state }) {
      const groups = await dispatch('groups/GET_GROUPS', null, { root: true });
      const response = await devicesService.find({
        query: {
          $skip: state.devices.length
        }
      });
      const devices = response.data.map(device => {
        const i = groups.findIndex(g => g._id === device.groupId);
        device.groupName = i >= 0 ? groups[i].name : 'No group yet.';
        return device;
      });

      commit('ADD_MORE_DEVICES', devices);
    },
    async GET_DEVICE({ dispatch }, _id) {
      const device = await devicesService.get(_id);
      let schedules = [];
      if (device.groupId) {
        const response = await dispatch('schedules/SEARCH_DEVICE_GROUP', device.groupId, {
          root: true
        });
        if (Array.isArray(response)) {
          schedules = [...response];
        }
      }
      return { ...device, schedules };
    },
    async ADD_DEVICE({ rootState, commit }, device) {
      try {
        const result = await devicesService.find({
          query: {
            serial: device.serial
          }
        });

        if (result.data.length > 0) {
          throw {
            success: false,
            message: 'Serial already exists'
          };
          return;
        }

        device.created_at = Date.now();
        const response = await devicesService.create(device);
        const groups = rootState.groups.groups;
        const i = groups.findIndex(g => g._id === response.groupId);
        response.groupName = i >= 0 ? groups[i].name : 'No group yet.';
        commit('ADD_DEVICE', response);
        return response;
      } catch (e) {
        throw e;
      }
    },
    async UPDATE_DEVICE({ rootState, commit }, payload) {
      try {
        payload.device.created_at = Date.now();
        delete payload.device._id;
        delete payload.device.groupName;
        delete payload.device.licensed;
        const response = await devicesService.patch(payload._id, payload.device);
        const groups = rootState.groups.groups;
        const i = groups.findIndex(g => g._id === response.groupId);
        response.groupName = i >= 0 ? groups[i].name : 'No group yet.';
        commit('UPDATE_DEVICE', response);
        return response;
      } catch (e) {
        throw e;
      }
    },
    async UPDATE_DEVICES_GROUP({ commit }, payload) {
      try {
        for (let device of payload.devices) {
          if (device.groupId != payload.newGroupId) {
            device.groupId = payload.newGroupId;

            const response = await devicesService.patch(device._id, { groupId: device.groupId });

            commit('UPDATE_DEVICE', response);
          }
        }
      } catch (error) {
        throw error;
      }
    },
    async DELETE_DEVICE({ commit }, _id) {
      try {
        // console.log("BEFORE_DELETE_DEVICE: ", _id);
        await devicesService.remove(_id);
        commit('REMOVE_DEVICE', _id);
        // console.log("DELETED_DEVICE: ", _id);
      } catch (e) {
        throw e;
      }
    },
    async DELETE_DEVICES({ commit }, devices) {
      const result = {
        success: [],
        failed: []
      };
      try {
        for (let device of devices) {
          //device online => cannot delete
          if (device.connected > device.disconnected) {
            result.failed.push(device.name || device._id);
            continue;
          }
          //device offline => cannot delete
          await devicesService.remove(device._id);

          commit('REMOVE_DEVICE', device._id);
          result.success.push(device.name || device._id);
        }
        return result;
      } catch (error) {
        throw error;
      }
    },
    async UPLOAD_UPGRADE_FILE({ dispatch }, payload) {
      try {
        const formData = new FormData();
        formData.append('file', payload);

        return await axios.post(
          `${config.server_url}/api/device-upgrade/uploadinstaller`,
          formData
        );
      } catch (error) {
        return { success: false, err: error };
      }
    },
    async FETCH_DEVICES_PAGINATED(
      { commit, dispatch },
      {
        pageIndex = 1,
        pageSize = 10,
        search = '',
        status = 'all',
        sortBy = null,
        descending = false
      } = {}
    ) {
      const limit = pageSize === -1 ? 9999 : pageSize;
      const params = {
        venueId: getSelectedVenue()._id,
        $skip: (pageIndex - 1) * limit,
        $limit: limit
      };

      if (search) params.$search = search;
      if (status === 'connected') params.$connected = true;
      if (status === 'disconnected') params.$connected = false;

      if (sortBy) {
        const fieldMap = {
          serial: 'serial',
          name: 'name',
          location: 'location',
          groupName: 'groupName',
          version: 'version',
          os_variant: 'os_variant',
          address: 'address',
          showInDashboard: 'show_in_dashboard'
        };
        const dbField = fieldMap[sortBy] || sortBy;
        params[`$sort[${dbField}]`] = descending ? -1 : 1;
      }

      const { data } = await apiClient.get('/devices', { params });

      const groups = await dispatch('groups/GET_GROUPS', null, { root: true });
      const groupMap = Object.fromEntries(groups.map(g => [g._id, g.name]));

      const devices = (data.data || []).map(device => ({
        ...device,
        _id: device._id || device.id,
        groupName: groupMap[device.groupId] || '_'
      }));

      commit('SET_PAGINATED_DEVICES', { data: devices, total: data.total });
      return { data: devices, total: data.total };
    },
    FETCH_PRESENCE: async function(context) {
      try {
        var res = await apiClient.get('/api/device-presence');
        context.commit('SET_PRESENCE', res.data.online || {});
      } catch (e) {
        console.error('[presence] fetch failed:', e);
      }
    },
    WATCH_PRESENCE: function(context) {
      import('@/services').then(function(module) {
        var signageSocket = module.signageSocket;

        signageSocket.off('presence:changed');

        // Batch rapid-fire presence:changed events (e.g. many devices
        // reconnecting/connecting close together) into one store commit every
        // 250ms instead of one commit+render cycle per event — a burst of
        // hundreds of individual commits in a few seconds is what was freezing
        // the devices table.
        var pendingPresenceUpdates = {};
        var presenceFlushTimer = null;

        function flushPresenceUpdates() {
          var updates = pendingPresenceUpdates;
          pendingPresenceUpdates = {};
          presenceFlushTimer = null;
          context.commit('UPDATE_PRESENCE_BATCH', updates);
        }

        signageSocket.on('presence:changed', function(data) {
          pendingPresenceUpdates[data.serial] = data.online;
          if (!presenceFlushTimer) {
            presenceFlushTimer = setTimeout(flushPresenceUpdates, 250);
          }
        });

        signageSocket.on('reconnect', function() {
          context.dispatch('FETCH_PRESENCE');
        });
      });
    }
  }
};

export default devices;



// WEBPACK FOOTER //
// ./src/store/modules/devices.js